import React, { ForwardedRef, forwardRef } from 'react'
import cn from 'classnames'
import { useFormControlContext } from './form-control-context'
import { CountryOptions } from './country-options'
import styles from './form.module.scss'
import { IconDropdownArrow } from '../icons/dropdown-arrow'
import { getSelectOption } from '@components/inputs/utils'

interface SelectProps {
  className?: string
  onBlur?: React.FocusEventHandler<HTMLSelectElement>
  onChange: React.ChangeEventHandler<HTMLSelectElement>
  // Id of the element that provides an additional description for the select field
  ariaDescribedBy?: string

  // When using Select inside a FormControl group, these props may be omitted.
  isDisabled?: boolean
  isInvalid?: boolean
  isRequired?: boolean
  name?: string
  id?: string
  options: string[]
}

export const Select = forwardRef((props: SelectProps, ref) => {
  const { nameContext, idContext, isDisabledContext, isRequiredContext, isInvalidContext } = useFormControlContext()

  const {
    className,
    isDisabled = isDisabledContext,
    isInvalid = isInvalidContext,
    isRequired = isRequiredContext,
    name = nameContext,
    id = idContext,
    ariaDescribedBy,
    onBlur,
    onChange,
    options,
  } = props

  const selectProps = {
    ...(isDisabled ? { disabled: true } : {}),
    ...(isInvalid ? { 'aria-invalid': true } : {}),
    ...(isRequired ? { 'aria-required': true } : {}),
    ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
    className: cn(styles.select, className, isInvalid && styles.inputError),
    id,
    name,
    onBlur,
    onChange,
  }

  const selectOptions = options.map(getSelectOption)

  return (
    <div className={styles.selectWrapper}>
      <select {...selectProps} ref={ref as ForwardedRef<HTMLSelectElement>}>
        <option value=""></option>

        {selectOptions.map(({ value, text }) => {
          if (value === 'countries') {
            return <CountryOptions key={value} />
          }

          return (
            <option value={value} key={value}>
              {text}
            </option>
          )
        })}
      </select>
      <IconDropdownArrow />
    </div>
  )
})
